"use client";

import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useRouter } from "next/navigation";
import { Drawer } from "vaul";

function Modal(props) {
  const {
    children,
    className,
    showModal,
    setShowModal,
    onClose,
    desktopOnly,
    preventDefaultClose,
    drawerRootProps,
    description,
    title = "Dialog",
  } = props;

  const router = useRouter();
  const { isMobile } = useMediaQuery();

  const closeModal = ({ dragged } = {}) => {
    if (preventDefaultClose && !dragged) return;

    if (onClose) onClose();

    if (setShowModal) {
      setShowModal(false);
    } else {
      router.back();
    }
  };

  if (isMobile && !desktopOnly) {
    return (
      <Drawer.Root
        open={setShowModal ? showModal : true}
        onOpenChange={(open) => {
          if (!open) {
            closeModal({ dragged: true });
          }
        }}
        {...drawerRootProps}
      >
        <Drawer.Portal>
          <Drawer.Overlay className="bg-opacity-10 fixed inset-0 z-50 bg-black/30 backdrop-blur-xs" />
          <Drawer.Content
            onPointerDownOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target.closest("[data-sonner-toast]")
              ) {
                e.preventDefault();
              }
            }}
            className={cn(
              "fixed right-0 bottom-0 left-0 z-50 flex flex-col",
              "rounded-t-[10px] border-t border-gray-200 bg-white",
              className,
            )}
          >
            <div className="scrollbar-hide flex-1 overflow-y-auto rounded-t-[10px] bg-inherit">
              <Drawer.Title className="sr-only">{title}</Drawer.Title>
              <DrawerIsland />
              {children}
            </div>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <Dialog.Root
      open={setShowModal ? showModal : true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in bg-opacity-50 fixed inset-0 z-40 bg-black/30 backdrop-blur-xs" />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            if (
              e.target instanceof Element &&
              e.target.closest("[data-sonner-toast]")
            ) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (e.target.closest(".pac-container")) {
              e.preventDefault();
            }
          }}
          className={cn(
            "fixed inset-0 z-40 m-auto h-fit w-full",
            "border border-gray-200 bg-white p-0 shadow-xl sm:rounded-2xl",
            "scrollbar-hide animate-scale-in overflow-y-auto outline-none",
            className,
          )}
        >
          <Dialog.Title className="sr-only">{title}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DrawerIsland() {
  return (
    <div className="sticky top-0 z-20 flex items-center justify-center rounded-t-[10px] bg-inherit">
      <div className="my-3 h-1 w-12 rounded-full bg-gray-300" />
    </div>
  );
}

const ModalTitle = ({ ref, title, onClose, description }) => (
  <div ref={ref} className="bg-white px-6 py-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 pr-8 sm:pr-0">
        <h3 className="!mt-0 text-lg font-medium break-words sm:truncate">
          {title}
        </h3>
      </div>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onClose}
          className="group hidden cursor-pointer rounded-full p-2 text-gray-500 transition-all duration-75 hover:bg-gray-100 focus:outline-hidden active:bg-gray-200 md:block"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
    {description && (
      <p className="w-[90%] text-sm text-gray-500">{description}</p>
    )}
  </div>
);
ModalTitle.displayName = "ModalTitle";

const ModalFooter = ({ ref, children }) => (
  <div ref={ref} className="mt-3 border-t border-gray-200 bg-gray-50 p-4">
    {children}
  </div>
);
ModalFooter.displayName = "ModalFooter";

export { Modal, ModalFooter, ModalTitle };
